/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  Avatar,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ConfirmDelete from "components/ConfirmDelete";
import { useAccountState } from "../accountContext";
import EditAccountBtn from "../Buttons/AddOrEditAccount";
import { Account } from "@dashboard-v3/api";
import ItemContent from "./ItemContent";
import useApprovalGroups from "./useApprovalGroups";
import TruncatedText from "components/TruncatedText";

export default function Item({ account }: { account: Account }) {
  const { t } = useTranslation("accounts");
  const { actions } = useAccountState();
  const { deleteAccount } = actions;
  const { email, aliases, sourceType, sourceLabel, storage, tags } = account;

  const {
    isLoading,
    approvalGroups,
    initLoad: loadApprovalGroups,
  } = useApprovalGroups(account);

  return (
    <ListItem disableGutters>
      <Accordion
        style={{ width: "100%" }}
        TransitionProps={{ unmountOnExit: true }}
        onChange={(_, expanded) => {
          if (expanded) loadApprovalGroups();
        }}
      >
        <AccordionSummary
          data-testid="accounts__account-summary"
          expandIcon={<ExpandMoreIcon />}
        >
          <ListItemIcon>
            <StyledAvatar>{email.charAt(0)}</StyledAvatar>
          </ListItemIcon>
          <ListItemText
            primary={<TruncatedText text={email} maxWidth={600} />}
            secondary={
              !isManualSource(sourceType) && (
                <span style={{ textTransform: "capitalize" }}>
                  {t("source")}: {t(`sourceTypes.${sourceType}`)}{" "}
                  {sourceLabel ? ` - ${sourceLabel}` : ""}
                </span>
              )
            }
          />
          <ListItemSecondaryAction style={{ marginRight: "35px" }}>
            <Box display="flex" alignItems="center">
              {canDelete(sourceType) && (
                <ConfirmDelete
                  onAccept={() => deleteAccount(email)}
                  message={t("buttons.confirmAccountDelete", { name: email })}
                  variant="outlined"
                />
              )}

              <EditAccountBtn
                variant="outlined"
                style={{ marginLeft: "10px" }}
                accountToEdit={{
                  email,
                  storage,
                  tags,
                  aliases,
                  approvalGroups: approvalGroupIds(account),
                }}
              />
            </Box>
          </ListItemSecondaryAction>
        </AccordionSummary>
        <StyledAccordionDetails style={{ display: "block" }}>
          <ItemContent
            account={account}
            approvalGroups={isLoading ? undefined : approvalGroups}
            validateStorage
          />
        </StyledAccordionDetails>
      </Accordion>
    </ListItem>
  );
}

const isManualSource = (sourceType: Account["sourceType"]) =>
  sourceType === "MANUAL";

const canDelete = (sourceType: Account["sourceType"]) =>
  !sourceType || isManualSource(sourceType);

const StyledAvatar = styled(Avatar)`
  align-self: center;
  text-transform: uppercase;
`;

const StyledAccordionDetails = styled(AccordionDetails)`
  display: block;
  padding-bottom: 0;
  padding-top: 0;
`;

function approvalGroupIds(account: Account) {
  const approvalGroupIds = [];
  if (account.approvalGroup) {
    approvalGroupIds.push(account.approvalGroup);
  }
  if (account.approvalGroups) {
    approvalGroupIds.push(...account.approvalGroups);
  }

  return approvalGroupIds;
}
